import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Loader2, RefreshCw, Download, Wand2, ExternalLink, AlertTriangle, TrendingUp, TrendingDown, CheckCircle2, XCircle, Mail, Image as ImageIcon, Undo2, ShieldAlert } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const money = (v) => v == null ? '—' : `R${Number(v).toLocaleString()}`;
const markedUp = (aqua, pct) => aqua == null ? null : Math.round(Number(aqua) * (1 + Number(pct) / 100));

const StatChip = ({ label, value, tone = 'default' }) => {
  const tones = {
    default: 'bg-gray-100 text-gray-800',
    good: 'bg-emerald-100 text-emerald-800',
    warn: 'bg-amber-100 text-amber-800',
    alert: 'bg-red-100 text-red-800',
  };
  return (
    <div className={`px-3 py-2 rounded-md ${tones[tone]}`}>
      <div className="text-[10px] uppercase tracking-widest font-semibold opacity-70">{label}</div>
      <div className="text-xl font-bold">{value}</div>
    </div>
  );
};

const TabButton = ({ active, count, onClick, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${active ? 'border-[#FF6B35] text-[#0A2342]' : 'border-transparent text-gray-500 hover:text-gray-800'}`}
    data-testid={testid}
  >
    {children} <span className="ml-1 text-xs opacity-60">({count})</span>
  </button>
);

const AquaDiversCompareCard = ({ token }) => {
  const [loading, setLoading] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [data, setData] = useState(null);
  const [markup, setMarkup] = useState(15);
  const [applyPrice, setApplyPrice] = useState(true);
  const [applyStock, setApplyStock] = useState(true);
  const [applyImages, setApplyImages] = useState(false);
  const [applySaleFlag, setApplySaleFlag] = useState(false);
  const [applyVariants, setApplyVariants] = useState(true);
  const [addMissing, setAddMissing] = useState(false);
  const [tab, setTab] = useState('mismatches');
  // Weekly-sync settings
  const [weeklyEnabled, setWeeklyEnabled] = useState(true);
  const [weeklyRecipients, setWeeklyRecipients] = useState('');
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  // Emergency cleanup state
  const [aquaSourced, setAquaSourced] = useState(null);
  const [aquaSourcedLoading, setAquaSourcedLoading] = useState(false);
  const [selectedForClear, setSelectedForClear] = useState(new Set());
  const [clearing, setClearing] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [reverting, setReverting] = useState(null);
  // Broken-image scanner state
  const [brokenScan, setBrokenScan] = useState(null);
  const [brokenScanning, setBrokenScanning] = useState(false);
  const [brokenClearing, setBrokenClearing] = useState(false);

  const loadAquaSourced = async () => {
    setAquaSourcedLoading(true);
    try {
      const r = await axios.get(`${API_URL}/admin/catalog-sync/aqua-divers/aqua-sourced-products`, { headers: { Authorization: `Bearer ${token}` } });
      setAquaSourced(r.data);
      setSelectedForClear(new Set());
    } catch (e) {
      toast.error('Load failed');
    } finally {
      setAquaSourcedLoading(false);
    }
  };

  const toggleClear = (id) => {
    setSelectedForClear(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAllForClear = () => setSelectedForClear(new Set((aquaSourced?.products || []).map(p => p.id)));
  const selectNoneForClear = () => setSelectedForClear(new Set());

  const clearSelectedImages = async () => {
    if (selectedForClear.size === 0) { toast.error('Pick at least one product'); return; }
    if (!window.confirm(`Clear the image on ${selectedForClear.size} product(s)? Their image slot will go blank until you re-upload.`)) return;
    setClearing(true);
    try {
      const r = await axios.post(`${API_URL}/admin/catalog-sync/aqua-divers/clear-aqua-images`, { ids: Array.from(selectedForClear) }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Cleared ${r.data.cleared} image(s)`);
      await loadAquaSourced();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Clear failed');
    } finally {
      setClearing(false);
    }
  };

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const r = await axios.get(`${API_URL}/admin/catalog-sync/aqua-divers/fix-history`, { headers: { Authorization: `Bearer ${token}` } });
      setHistory(r.data.runs || []);
    } catch (e) {
      toast.error('History load failed');
    } finally {
      setHistoryLoading(false);
    }
  };

  const revertRun = async (id) => {
    if (!window.confirm('Revert this Fix All run? All fields will be restored to their previous values, and any products created by this run will be deleted.')) return;
    setReverting(id);
    try {
      const r = await axios.post(`${API_URL}/admin/catalog-sync/aqua-divers/revert/${id}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Reverted: ${r.data.products_restored} restored, ${r.data.products_deleted} deleted`);
      await loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Revert failed');
    } finally {
      setReverting(null);
    }
  };

  // Broken image scan — HEAD-checks every product image_url against its host
  // and returns anything that 404s / times out / isn't actually an image.
  const scanBrokenImages = async (autoClear = false) => {
    if (autoClear && !window.confirm('Scan every product image and CLEAR the URL on any that\'s broken? Cleared products will fall back to the storefront placeholder until you re-upload.')) return;
    if (autoClear) setBrokenClearing(true); else setBrokenScanning(true);
    try {
      const r = await axios.post(
        `${API_URL}/admin/catalog-sync/scan-broken-images`,
        { clear: autoClear },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 180000 },
      );
      setBrokenScan(r.data);
      if (autoClear) toast.success(`Cleared ${r.data.cleared} broken image URL(s)`);
      else toast.success(`Scanned ${r.data.scanned}. Broken: ${r.data.broken_count}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Scan failed');
    } finally {
      setBrokenScanning(false);
      setBrokenClearing(false);
    }
  };

  useEffect(() => {
    axios.get(`${API_URL}/site-content`)
      .then((r) => {
        const s = r.data || {};
        if (s.aqua_sync_markup_percent) setMarkup(Number(s.aqua_sync_markup_percent) || 15);
        setWeeklyEnabled(String(s.aqua_sync_weekly_enabled ?? 'true').toLowerCase() !== 'false');
        setWeeklyRecipients(s.aqua_sync_recipients || 'wayne@justdive.co.za,richard@justdive.co.za');
      })
      .catch(() => {})
      .finally(() => setSettingsLoaded(true));
  }, []);

  const saveWeeklySettings = async () => {
    setSettingsSaving(true);
    try {
      await axios.put(`${API_URL}/admin/site-content/bulk`, [
        { key: 'aqua_sync_weekly_enabled', value: weeklyEnabled ? 'true' : 'false' },
        { key: 'aqua_sync_markup_percent', value: String(markup || 15) },
        { key: 'aqua_sync_recipients', value: weeklyRecipients || '' },
      ], { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Weekly sync settings saved');
    } catch (e) {
      toast.error('Save failed');
    } finally {
      setSettingsSaving(false);
    }
  };

  const sendTestEmail = async () => {
    if (!window.confirm(`Send a fresh weekly sync email now to:\n\n${weeklyRecipients || '(default recipients)'}\n\nThis will run the live scrape (~20 s) and deliver an Excel attachment.`)) return;
    setSendingTest(true);
    try {
      const r = await axios.post(`${API_URL}/admin/catalog-sync/aqua-divers/send-weekly-email`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 180000,
      });
      const sentTo = r.data.sent_to || [];
      const failed = r.data.failed_to || [];
      if (sentTo.length) toast.success(`Weekly email sent to ${sentTo.join(', ')}`);
      if (failed.length) toast.error(`Failed to send to ${failed.join(', ')}`);
      if (!sentTo.length && !failed.length) toast.info('No recipients configured');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    } finally {
      setSendingTest(false);
    }
  };

  const runCompare = async () => {
    setLoading(true);
    setData(null);
    try {
      const r = await axios.post(`${API_URL}/admin/catalog-sync/aqua-divers/compare`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 120000,
      });
      setData(r.data);
      toast.success(`Compared: ${r.data.summary.matched_count} matched, ${r.data.summary.price_mismatches} price diffs, ${r.data.summary.aqua_only_count} new on Aqua`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Aqua Divers scrape failed. Try again in a moment.');
    } finally {
      setLoading(false);
    }
  };

  const runFixAll = async () => {
    const confirmMsg = `Apply the following to your catalogue?\n\n` +
      `• Markup: ${markup}% on Aqua's base price\n` +
      `• Update prices: ${applyPrice ? 'yes' : 'no'}\n` +
      `• Update stock flags: ${applyStock ? 'yes' : 'no'}\n` +
      `• Copy Aqua images (where different): ${applyImages ? 'yes' : 'no'}\n` +
      `• Apply sale/strikethrough badge: ${applySaleFlag ? 'yes' : 'no'}\n` +
      `• Sync size / colour options: ${applyVariants ? 'yes' : 'no'}\n` +
      `• Create missing products: ${addMissing ? 'yes' : 'no'}\n\n` +
      `Aqua "Special" items with hidden prices are skipped automatically.`;
    if (!window.confirm(confirmMsg)) return;
    setFixing(true);
    try {
      const r = await axios.post(`${API_URL}/admin/catalog-sync/aqua-divers/fix-all`, {
        markup_percent: Number(markup),
        apply_price: applyPrice,
        apply_stock: applyStock,
        apply_images: applyImages,
        apply_sale_flag: applySaleFlag,
        apply_variants: applyVariants,
        add_missing: addMissing,
      }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 180000,
      });
      toast.success(`Applied: ${r.data.price_updates} prices, ${r.data.stock_updates} stock, ${r.data.image_updates} images, ${r.data.sale_flag_updates} sale flags, ${r.data.variant_updates} variants, ${r.data.created} new`);
      // Refresh
      await runCompare();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fix All failed');
    } finally {
      setFixing(false);
    }
  };

  const exportExcel = async () => {
    setExporting(true);
    try {
      const r = await axios.post(`${API_URL}/admin/catalog-sync/aqua-divers/export.xlsx?markup_percent=${Number(markup)}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
        timeout: 120000,
      });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `aqua_divers_comparison_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel comparison downloaded');
    } catch (e) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const summary = data?.summary;
  const mismatches = (data?.matched || []).filter(m => m.price_mismatch || m.stock_mismatch || m.image_mismatch);
  const identical = (data?.matched || []).filter(m => !m.price_mismatch && !m.stock_mismatch && !m.image_mismatch && !m.aqua_price_hidden);
  const hidden = (data?.matched || []).filter(m => m.aqua_price_hidden);
  const imageDiffs = (data?.matched || []).filter(m => m.image_mismatch);
  const aquaOnly = data?.aqua_only || [];
  const ourOnly = data?.our_only || [];

  const listForTab = {
    mismatches, identical, hidden, image_diffs: imageDiffs, aqua_only: aquaOnly, our_only: ourOnly,
  }[tab] || [];

  return (
    <div className="bg-white rounded-lg border p-6" data-testid="aqua-compare-card">
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div>
          <h3 className="font-semibold text-[#0A2342] mb-1 flex items-center gap-2">
            <RefreshCw className="w-4 h-4" /> Aqua Divers ↔ Just Dive stock comparison
          </h3>
          <p className="text-xs text-gray-500 max-w-2xl">
            Live scrape of <a href="https://www.aquadivers.co.za" target="_blank" rel="noopener noreferrer" className="underline">aquadivers.co.za</a> compared against your own catalogue. Prices, stock flags, missing SKUs all in one place. Fix All applies your markup automatically. Scrape takes about 20-30 seconds.
          </p>
        </div>
        <Button
          onClick={runCompare}
          disabled={loading}
          className="bg-[#0A2342] hover:bg-[#0A2342]/90 text-white shrink-0"
          data-testid="aqua-compare-run"
        >
          {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Scraping…</> : <><RefreshCw className="w-4 h-4 mr-2" /> Run comparison</>}
        </Button>
      </div>

      {/* Config bar */}
      <div className="flex items-end gap-3 mb-4 flex-wrap p-3 bg-gray-50 rounded-md border">
        <div className="flex-1 min-w-[140px]">
          <label className="block text-[11px] font-semibold text-gray-700 mb-1">Markup on Aqua's price (%)</label>
          <Input type="number" min={0} max={200} step={1} value={markup} onChange={(e) => setMarkup(e.target.value)} className="h-9" data-testid="aqua-markup" />
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700 h-9">
          <input type="checkbox" checked={applyPrice} onChange={(e) => setApplyPrice(e.target.checked)} data-testid="aqua-apply-price" /> Apply prices
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-700 h-9">
          <input type="checkbox" checked={applyStock} onChange={(e) => setApplyStock(e.target.checked)} data-testid="aqua-apply-stock" /> Apply stock
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-700 h-9">
          <input type="checkbox" checked={applyImages} onChange={(e) => setApplyImages(e.target.checked)} data-testid="aqua-apply-images" /> Apply images
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-700 h-9">
          <input type="checkbox" checked={applySaleFlag} onChange={(e) => setApplySaleFlag(e.target.checked)} data-testid="aqua-apply-sale" /> Apply sale badge
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-700 h-9">
          <input type="checkbox" checked={applyVariants} onChange={(e) => setApplyVariants(e.target.checked)} data-testid="aqua-apply-variants" /> Sync sizes/colours
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-700 h-9">
          <input type="checkbox" checked={addMissing} onChange={(e) => setAddMissing(e.target.checked)} data-testid="aqua-add-missing" /> Create missing
        </label>
        <div className="flex gap-2 ml-auto">
          <Button variant="outline" onClick={exportExcel} disabled={exporting || loading} data-testid="aqua-export">
            {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />} Export Excel
          </Button>
          <Button onClick={runFixAll} disabled={fixing || loading || !data} className="bg-[#FF6B35] hover:bg-[#FF6B35]/90 text-white" data-testid="aqua-fix-all">
            {fixing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Applying…</> : <><Wand2 className="w-4 h-4 mr-2" /> Fix All</>}
          </Button>
        </div>
      </div>

      {/* Weekly auto-sync email settings */}
      <div className="p-3 mb-4 bg-[#0A2342]/5 border border-[#0A2342]/20 rounded-md" data-testid="aqua-weekly-panel">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
          <div className="flex items-center gap-2 text-sm font-semibold text-[#0A2342]">
            <Mail className="w-4 h-4" /> Weekly auto-sync email
            <span className="text-[11px] font-normal text-gray-500">— every Monday at 06:30 SAST</span>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={weeklyEnabled} onChange={(e) => setWeeklyEnabled(e.target.checked)} data-testid="aqua-weekly-enabled" />
            {weeklyEnabled ? 'Enabled' : 'Paused'}
          </label>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div className="flex-1 min-w-[240px]">
            <label className="block text-[11px] font-semibold text-gray-700 mb-1">Recipients (comma-separated)</label>
            <Input
              value={weeklyRecipients}
              onChange={(e) => setWeeklyRecipients(e.target.value)}
              placeholder="wayne@justdive.co.za, richard@justdive.co.za"
              className="h-9 text-sm"
              data-testid="aqua-weekly-recipients"
            />
          </div>
          <Button variant="outline" onClick={saveWeeklySettings} disabled={settingsSaving || !settingsLoaded} data-testid="aqua-weekly-save">
            {settingsSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Save settings
          </Button>
          <Button onClick={sendTestEmail} disabled={sendingTest} className="bg-[#0A2342] hover:bg-[#0A2342]/90 text-white" data-testid="aqua-weekly-send-now">
            {sendingTest ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</> : <><Mail className="w-4 h-4 mr-2" /> Send email now</>}
          </Button>
        </div>
        <p className="text-[11px] text-gray-500 mt-2">
          The email includes a summary, the top 10 biggest price gaps, and the full 3-sheet Excel comparison as an attachment. The markup % above is what gets applied.
        </p>
      </div>

      {!data && !loading && (
        <div className="text-center py-12 border-2 border-dashed rounded-lg text-sm text-gray-500">
          Click <span className="font-semibold">Run comparison</span> to fetch live stock from aquadivers.co.za.
        </div>
      )}

      {loading && (
        <div className="text-center py-12 text-gray-500">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
          Scraping aquadivers.co.za (up to 30 s)…
        </div>
      )}

      {data && summary && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 mb-4">
            <StatChip label="Aqua total" value={summary.aqua_total} />
            <StatChip label="Our total" value={summary.our_total} />
            <StatChip label="Matched" value={summary.matched_count} tone="good" />
            <StatChip label="Price diffs" value={summary.price_mismatches} tone={summary.price_mismatches ? 'warn' : 'default'} />
            <StatChip label="Stock diffs" value={summary.stock_mismatches} tone={summary.stock_mismatches ? 'warn' : 'default'} />
            <StatChip label="Image diffs" value={summary.image_mismatches ?? 0} tone={(summary.image_mismatches ?? 0) ? 'warn' : 'default'} />
            <StatChip label="Aqua-only" value={summary.aqua_only_count} tone={summary.aqua_only_count ? 'warn' : 'default'} />
          </div>

          {summary.aqua_hidden_price > 0 && (
            <div className="flex items-start gap-2 p-3 mb-4 bg-amber-50 border border-amber-200 rounded-md">
              <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <div className="text-xs text-amber-900">
                <strong>{summary.aqua_hidden_price}</strong> of Aqua's products are "Special" listings with hidden prices (call-for-quote / in-store only). These are skipped by Fix All — check them manually if you want to stock them.
              </div>
            </div>
          )}

          {/* Emergency cleanup panel — for undoing bad Fix All runs */}
          <details className="mb-4 border border-red-200 rounded-md" data-testid="aqua-emergency-panel">
            <summary className="cursor-pointer p-3 bg-red-50 rounded-md flex items-center gap-2 text-sm font-semibold text-red-800">
              <ShieldAlert className="w-4 h-4" /> Emergency cleanup &amp; revert
              <span className="text-[11px] font-normal text-red-700">— undo a bad Fix All run or clear wrong images</span>
            </summary>
            <div className="p-4 space-y-4">
              {/* Section 1: Fix All history + revert */}
              <div>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <h4 className="text-sm font-semibold text-[#0A2342]">Recent Fix All runs</h4>
                  <Button size="sm" variant="outline" onClick={loadHistory} disabled={historyLoading} data-testid="aqua-history-load">
                    {historyLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null} Load history
                  </Button>
                </div>
                {history.length === 0 ? (
                  <p className="text-xs text-gray-500">Click Load history to see previous Fix All runs. Each one can be reverted with one click.</p>
                ) : (
                  <div className="space-y-2">
                    {history.map((h) => (
                      <div key={h._id} className={`flex items-center justify-between gap-2 p-2 border rounded ${h.reverted_at ? 'bg-gray-50 opacity-60' : 'bg-white'}`} data-testid={`aqua-history-${h._id}`}>
                        <div className="flex-1 min-w-0 text-xs">
                          <div className="font-mono text-gray-700">{h.run_at?.slice(0, 19).replace('T', ' ')}</div>
                          <div className="text-gray-500">
                            markup {h.markup_percent}% · prices {h.counts?.price} · stock {h.counts?.stock} · images {h.counts?.images} · sale {h.counts?.sale_flag} · created {h.counts?.created}
                          </div>
                          <div className="text-gray-400">by {h.triggered_by || 'admin'}</div>
                          {h.reverted_at && <div className="text-[10px] text-red-600 font-semibold mt-0.5">REVERTED {h.reverted_at.slice(0, 19).replace('T', ' ')}</div>}
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => revertRun(h._id)}
                          disabled={!!h.reverted_at || reverting === h._id}
                          data-testid={`aqua-revert-${h._id}`}
                        >
                          {reverting === h._id ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Undo2 className="w-3 h-3 mr-1" />} Revert
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Section 2: aqua-sourced products (raw cleanup) */}
              <div className="border-t pt-3">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <h4 className="text-sm font-semibold text-[#0A2342]">Products with images from aquadivers.co.za</h4>
                  <Button size="sm" variant="outline" onClick={loadAquaSourced} disabled={aquaSourcedLoading} data-testid="aqua-sourced-load">
                    {aquaSourcedLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null} Scan
                  </Button>
                </div>
                <p className="text-[11px] text-gray-500 mb-2">These are products whose image was set by a Fix All. Tick any that look wrong and click "Clear selected images" — the picture will go blank so you can re-upload the correct one.</p>
                {aquaSourced && (
                  <>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs text-gray-600">{aquaSourced.total} products found · {selectedForClear.size} selected</span>
                      <Button size="sm" variant="ghost" onClick={selectAllForClear}>Select all</Button>
                      <Button size="sm" variant="ghost" onClick={selectNoneForClear}>None</Button>
                      <Button
                        size="sm"
                        className="ml-auto bg-red-600 hover:bg-red-700 text-white"
                        onClick={clearSelectedImages}
                        disabled={clearing || selectedForClear.size === 0}
                        data-testid="aqua-clear-images"
                      >
                        {clearing ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null} Clear selected images
                      </Button>
                    </div>
                    <div className="max-h-[400px] overflow-y-auto border rounded">
                      {aquaSourced.products.map((p) => (
                        <label key={p.id} className={`flex items-center gap-3 p-2 border-b hover:bg-gray-50 cursor-pointer ${selectedForClear.has(p.id) ? 'bg-red-50' : ''}`}>
                          <input type="checkbox" checked={selectedForClear.has(p.id)} onChange={() => toggleClear(p.id)} data-testid={`aqua-sourced-select-${p.id}`} />
                          <img src={p.image_url} alt="" className="w-12 h-12 object-cover rounded border shrink-0" onError={(e) => { e.target.style.display = 'none'; }} />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">{p.name}</div>
                            <div className="text-[11px] text-gray-500 truncate">R{Number(p.price || 0).toLocaleString()} · {p.in_stock ? 'in stock' : 'out'}</div>
                            <div className="text-[10px] text-gray-400 truncate">{p.image_url}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </details>

          {/* Broken-image scanner — runs a HEAD check across every product
              image URL and lets the admin clear the broken ones in bulk.
              Solves the "Preview unavailable" cases in the Edit Product
              modal caused by dead external URLs. */}
          <details className="mb-4 border border-amber-200 rounded-md" data-testid="broken-image-panel">
            <summary className="cursor-pointer p-3 bg-amber-50 rounded-md flex items-center gap-2 text-sm font-semibold text-amber-900">
              <ShieldAlert className="w-4 h-4" /> Broken image scanner
              <span className="text-[11px] font-normal text-amber-800">— find and clear dead external image URLs</span>
            </summary>
            <div className="p-4 space-y-3">
              <p className="text-xs text-gray-600">
                HEAD-checks every product image URL against its host. Anything that returns 4xx/5xx, times out, or isn't an image is listed below. Use <em>Scan &amp; clear</em> to blank the broken ones — the storefront will fall back to the placeholder until you re-upload the correct picture.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => scanBrokenImages(false)} disabled={brokenScanning || brokenClearing} data-testid="broken-image-scan">
                  {brokenScanning ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null} Scan only
                </Button>
                <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white" onClick={() => scanBrokenImages(true)} disabled={brokenScanning || brokenClearing} data-testid="broken-image-scan-clear">
                  {brokenClearing ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null} Scan &amp; clear all broken
                </Button>
              </div>
              {brokenScan && (
                <div className="text-xs">
                  <div className="mb-2 text-gray-700">
                    Scanned <strong>{brokenScan.scanned}</strong> products · Broken: <strong className={brokenScan.broken_count > 0 ? 'text-red-600' : 'text-green-600'}>{brokenScan.broken_count}</strong>{brokenScan.cleared > 0 ? ` · Cleared: ${brokenScan.cleared}` : ''}
                  </div>
                  {brokenScan.broken_count > 0 && (
                    <div className="max-h-[300px] overflow-y-auto border rounded" data-testid="broken-image-list">
                      {brokenScan.broken.map((b) => (
                        <div key={`${b.collection}-${b.id}`} className="flex items-center gap-3 p-2 border-b last:border-b-0 bg-white">
                          <div className="w-1 h-8 bg-red-400 rounded shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">{b.name}</div>
                            <div className="text-[10px] text-red-700 font-mono truncate">{b.reason} · {b.image_url}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </details>

          <div className="border-b flex gap-1 mb-3 flex-wrap">
            <TabButton active={tab === 'mismatches'} onClick={() => setTab('mismatches')} count={mismatches.length} testid="aqua-tab-mismatches">Mismatches</TabButton>
            <TabButton active={tab === 'image_diffs'} onClick={() => setTab('image_diffs')} count={imageDiffs.length} testid="aqua-tab-image-diffs">Image diffs</TabButton>
            <TabButton active={tab === 'aqua_only'} onClick={() => setTab('aqua_only')} count={aquaOnly.length} testid="aqua-tab-aqua-only">They stock, we don't</TabButton>
            <TabButton active={tab === 'our_only'} onClick={() => setTab('our_only')} count={ourOnly.length} testid="aqua-tab-our-only">We stock, they don't</TabButton>
            <TabButton active={tab === 'identical'} onClick={() => setTab('identical')} count={identical.length} testid="aqua-tab-identical">Already in sync</TabButton>
            <TabButton active={tab === 'hidden'} onClick={() => setTab('hidden')} count={hidden.length} testid="aqua-tab-hidden">Hidden price</TabButton>
          </div>

          <div className="border rounded-md overflow-hidden max-h-[500px] overflow-y-auto">
            {listForTab.length === 0 ? (
              <div className="text-center py-8 text-sm text-gray-500">Nothing here — good news.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr className="text-left text-[11px] uppercase tracking-wider text-gray-500">
                    <th className="p-2">Product</th>
                    {tab === 'image_diffs' && (
                      <>
                        <th className="p-2">Ours</th>
                        <th className="p-2"></th>
                        <th className="p-2">Aqua</th>
                      </>
                    )}
                    {(tab === 'mismatches' || tab === 'identical' || tab === 'hidden') && (
                      <>
                        <th className="p-2 text-right">Ours</th>
                        <th className="p-2 text-right">Aqua</th>
                        <th className="p-2 text-right">Marked-up</th>
                        <th className="p-2 text-center">Stock</th>
                      </>
                    )}
                    {tab === 'aqua_only' && (
                      <>
                        <th className="p-2 text-right">Aqua price</th>
                        <th className="p-2 text-right">Marked-up</th>
                        <th className="p-2 text-center">Stock</th>
                      </>
                    )}
                    {tab === 'our_only' && (
                      <>
                        <th className="p-2 text-right">Our price</th>
                        <th className="p-2 text-center">Stock</th>
                      </>
                    )}
                    <th className="p-2 text-right">Link</th>
                  </tr>
                </thead>
                <tbody>
                  {listForTab.map((row, i) => {
                    const isMatched = tab === 'mismatches' || tab === 'identical' || tab === 'hidden';
                    const isImageDiff = tab === 'image_diffs';
                    const isAqua = tab === 'aqua_only';
                    const isOur = tab === 'our_only';
                    return (
                      <tr key={i} className="border-t hover:bg-gray-50" data-testid={`aqua-row-${tab}-${i}`}>
                        {isImageDiff && (
                          <>
                            <td className="p-2">
                              <div className="font-medium text-gray-900 text-sm">{row.our_name}</div>
                              {row.aqua_name !== row.our_name && <div className="text-[11px] text-gray-400">Aqua: {row.aqua_name}</div>}
                            </td>
                            <td className="p-2">
                              {row.our_image ? (
                                <img src={row.our_image} alt="ours" className="w-16 h-16 object-cover rounded border" onError={(e) => { e.target.style.display = 'none'; }} />
                              ) : <div className="w-16 h-16 rounded border border-dashed flex items-center justify-center text-[10px] text-gray-400">no image</div>}
                            </td>
                            <td className="p-2 text-center text-gray-400 text-2xl">→</td>
                            <td className="p-2">
                              {row.aqua_image ? (
                                <img src={row.aqua_image} alt="aqua" className="w-16 h-16 object-cover rounded border-2 border-[#FF6B35]" onError={(e) => { e.target.style.display = 'none'; }} />
                              ) : <div className="w-16 h-16 rounded border border-dashed flex items-center justify-center text-[10px] text-gray-400">no image</div>}
                            </td>
                            <td className="p-2 text-right">
                              {row.aqua_url && <a href={row.aqua_url} target="_blank" rel="noopener noreferrer" className="text-[#0A2342] hover:text-[#FF6B35] inline-flex items-center gap-1 text-xs">open <ExternalLink className="w-3 h-3" /></a>}
                            </td>
                          </>
                        )}
                        {isMatched && (
                          <>
                        <td className="p-2">
                          <div className="flex items-center gap-2">
                            {row.aqua_image && (
                              <img src={row.aqua_image} alt="" className="w-8 h-8 object-cover rounded border shrink-0" onError={(e) => { e.target.style.display = 'none'; }} />
                            )}
                            <div>
                              <div className="font-medium text-gray-900">{row.our_name}</div>
                              {row.aqua_name !== row.our_name && (
                                <div className="text-[11px] text-gray-400">Aqua name: {row.aqua_name}</div>
                              )}
                              {row.image_mismatch && (
                                <div className="text-[10px] text-[#FF6B35] font-semibold flex items-center gap-1 mt-0.5">
                                  <ImageIcon className="w-3 h-3" /> image differs
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                            <td className="p-2 text-right font-mono">{money(row.our_price)}</td>
                            <td className="p-2 text-right font-mono">
                              {row.aqua_price_hidden ? <span className="text-amber-700">hidden</span> : money(row.aqua_price)}
                            </td>
                            <td className="p-2 text-right font-mono">
                              {row.aqua_price_hidden ? '—' : (
                                <span className={row.price_mismatch ? 'font-semibold text-[#FF6B35]' : 'text-gray-500'}>
                                  {money(markedUp(row.aqua_price, markup))}
                                  {row.price_mismatch && (
                                    (markedUp(row.aqua_price, markup) || 0) > (row.our_price || 0)
                                      ? <TrendingUp className="w-3 h-3 inline ml-1" />
                                      : <TrendingDown className="w-3 h-3 inline ml-1" />
                                  )}
                                </span>
                              )}
                            </td>
                            <td className="p-2 text-center">
                              <span className="inline-flex items-center gap-1">
                                {row.our_in_stock ? <CheckCircle2 className="w-3 h-3 text-emerald-600" /> : <XCircle className="w-3 h-3 text-red-500" />}
                                <span className="text-gray-400">/</span>
                                {row.aqua_in_stock ? <CheckCircle2 className="w-3 h-3 text-emerald-600" /> : <XCircle className="w-3 h-3 text-red-500" />}
                                {row.stock_mismatch && <AlertTriangle className="w-3 h-3 text-amber-500 ml-1" />}
                              </span>
                            </td>
                          </>
                        )}
                        {isAqua && (
                          <>
                            <td className="p-2">
                              <div className="flex items-center gap-2">
                                {row.aqua_image && <img src={row.aqua_image} alt="" className="w-8 h-8 object-cover rounded border shrink-0" onError={(e) => { e.target.style.display = 'none'; }} />}
                                <span className="font-medium text-gray-900">{row.aqua_name}</span>
                              </div>
                            </td>
                            <td className="p-2 text-right font-mono">{(!row.aqua_price || row.aqua_price <= 0) ? <span className="text-amber-700">hidden</span> : money(row.aqua_price)}</td>
                            <td className="p-2 text-right font-mono">{(!row.aqua_price || row.aqua_price <= 0) ? '—' : money(markedUp(row.aqua_price, markup))}</td>
                            <td className="p-2 text-center">{row.aqua_in_stock ? <CheckCircle2 className="w-3 h-3 text-emerald-600 inline" /> : <XCircle className="w-3 h-3 text-red-500 inline" />}</td>
                          </>
                        )}
                        {isOur && (
                          <>
                            <td className="p-2">
                              <div className="flex items-center gap-2">
                                {row.our_image && <img src={row.our_image} alt="" className="w-8 h-8 object-cover rounded border shrink-0" onError={(e) => { e.target.style.display = 'none'; }} />}
                                <span className="font-medium text-gray-900">{row.our_name}</span>
                              </div>
                            </td>
                            <td className="p-2 text-right font-mono">{money(row.our_price)}</td>
                            <td className="p-2 text-center">{row.our_in_stock ? <CheckCircle2 className="w-3 h-3 text-emerald-600 inline" /> : <XCircle className="w-3 h-3 text-red-500 inline" />}</td>
                          </>
                        )}
                        {!isImageDiff && (
                          <td className="p-2 text-right">
                            {row.aqua_url && (
                              <a href={row.aqua_url} target="_blank" rel="noopener noreferrer" className="text-[#0A2342] hover:text-[#FF6B35] inline-flex items-center gap-1 text-xs">
                                open <ExternalLink className="w-3 h-3" />
                              </a>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AquaDiversCompareCard;
